import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
    Save,
    X,
    FileText,
    CheckSquare,
    CheckCircle2,
    Clock,
    Download,
    Mail,
    Printer,
    BarChart3,
    User,
    ChevronDown,
    ChevronUp,
    Search,
    ArrowLeft,
    Target,
    TrendingUp,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";


import {
    getImportStatusAuditDetails,
    updateImportStatusAuditDetails,
} from "@/redux/ImportBooking/actions";
import { callLoader } from "@/redux/Loader/actions";
import DateInputwithcal from "@/components/ui/DateInputwithcal";
import { deepClone } from "@/utility/CommonConstant";
import CelebrationButton from "@/components/ui/CelebrationButton/CelebrationButton";
import { showToast } from "@/redux/Toast/actions";
import notificationService from "@/services/notificationService";
import { ImportStatusAuditGroup } from "@/data/ImportStatusAuditGroup";
import moment from "moment";
import PageHeader from "@/components/FormHeader/formPageHeader";
import AIInsightCard from "@/components/shipment/AIInsightCard";

const ImportStatusAudit = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { state } = location;
    const params = useParams();
    const searchParams = new URLSearchParams(location.search);
    const dispatch = useDispatch();

    const getAuthUser = () => {
        try {
            const data = localStorage.getItem("authUser");
            return data ? JSON.parse(data) : {};
        } catch {
            return {};
        }
    };

    const authUser = getAuthUser();



    const bookingId = state?.bookingId || params.id;
    const companyId = state?.companyId || searchParams.get("companyId") || authUser?.companyId;

    console.log("companyId:", companyId); // Debug log for companyId
    console.log("bookingId:", bookingId); // Debug log for bookingId

    const [searchQuery, setSearchQuery] = useState("");
    const [filterStatus, setFilterStatus] = useState("all");
    const [groupedData, setGroupedData] = useState({});
    const [auditData, setAuditData] = useState({});

    const [expandedGroups, setExpandedGroups] = useState({
        BookingStage: true,
        HBLProcessing: true,
        FilingStage: true,
        ShipmentStage: true,
        DocumentationStage: true,
        ArrivalStage: true,
        ClearanceStage: true,
        InvoiceStage: true,
        DeliveryStage: true,
        ContainerMovement: true,
        ClosureStage: true,
    });

    const [updateAuditData, setUpdateAuditData] = useState({});
    const [printData, setPrintData] = useState({});


    const [displayData, setDisplayData] = useState(() => ({
        reportNumber: state?.FILENUMBER || "",
        carrierBookingNumber: state?.carrierBookingNumber || "", //
        bookingContactPerson: state?.customerName || "",
        bookingEnteredBy: state?.createdBy || "",
        bookingEnteredDate: state?.createdOn
            ? moment(state.createdOn).format("D MMM YYYY [AT] hh:mm A")
            : "",
    }));

    // ✅ Rename selector param from `state` to `store` to avoid shadowing
    const { MasterCompanyDetails } = useSelector((store) => store.common);
    const { isAnimate } = useSelector((store) => store.toast);

    // ─── Mirrors StatusAudit's handleCheckboxChange exactly ──────────────────────
    const handleCheckboxChange = (groupKey, index) => {
        setGroupedData((prev) => {
            const updated = { ...prev };
            const group = [...updated[groupKey]];
            const item = { ...group[index] };

            item.checked = !item.checked;

            if (item.checked && !item.date) {
                const now = new Date();
                item.date = now.toISOString().split("T")[0];
                item.time = now.toTimeString().slice(0, 5);
            } else if (!item.checked) {
                item.date = "";
                item.time = "";
            }

            group[index] = item;
            updated[groupKey] = group;
            return { ...updated };
        });
    };

    const handleDateChange = (groupKey, index, field, value) => {
        setGroupedData((prev) => {
            const updated = { ...prev };
            const group = [...updated[groupKey]];
            group[index] = { ...group[index], [field]: value };
            updated[groupKey] = group;
            return { ...updated };
        });
    };

    const allStatuses = useMemo(() => {
        return Object.values(groupedData || {}).flat();
    }, [groupedData]);

    const completedCount = useMemo(
        () => allStatuses.filter((s) => s.checked).length,
        [allStatuses]
    );

    const totalCount = useMemo(() => allStatuses.length, [allStatuses]);

    const pendingCount = useMemo(
        () => totalCount - completedCount,
        [totalCount, completedCount]
    );

    const progressPercent = useMemo(() => {
        if (!totalCount) return 0;
        return Math.round((completedCount / totalCount) * 100);
    }, [completedCount, totalCount]);

    const getFilteredGroupStatuses = (groupKey) => {
        const group = groupedData[groupKey] || [];
        return group.filter((status) => {
            const matchesSearch = status.label
                .toLowerCase()
                .includes(searchQuery.toLowerCase());
            const matchesFilter =
                filterStatus === "all" ||
                (filterStatus === "completed" && status.checked) ||
                (filterStatus === "pending" && !status.checked);
            return matchesSearch && matchesFilter;
        });
    };

    const getGroupCounts = (groupKey) => {
        const group = groupedData[groupKey] || [];
        const completed = group.filter((s) => s.checked).length;
        const total = group.length;
        return { completed, total };
    };



    const formatLabel = (key) =>
        key.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase());

    // ─── FIX 2: null guard mirrors StatusAudit's resultData?.[0] || defaultData ──
    const buildGroupedData = (data) => {
        const safeData = data || {};
        const grouped = {};
        Object.entries(ImportStatusAuditGroup).forEach(([groupKey, group]) => {
            grouped[groupKey] = group.keys.map((key) => {
                const dateTime = safeData[`${key}_dt`];
                let date = "";
                let time = "";
                if (dateTime && dateTime !== "nullZ") {
                    const dt = new Date(dateTime);
                    date = dt.toISOString().split("T")[0];
                    time = dt.toTimeString().slice(0, 5);
                }
                return {
                    id: key,
                    label: formatLabel(key),
                    checked: safeData[key] || false,
                    date,
                    time,
                    by: safeData[`${key}_by`] || "",
                };
            });
        });
        return grouped;
    };

    const _getStatusAuditDetails = async (bookingid, companyId) => {
        try {
            dispatch(callLoader(1));
            const rawResult = await dispatch(
                getImportStatusAuditDetails(bookingid, companyId)
            );

            console.log("rawResult from API:", rawResult); // Debug log to inspect API response

            const resultData = rawResult?.[0] ?? rawResult ?? {};

            const grouped = buildGroupedData(resultData);
            setAuditData(resultData);
            setGroupedData(grouped);
            setPrintData(deepClone({ ...resultData, groupedStatuses: grouped }));

            // ─── FIX 4: Populate displayData (used in JSX below) ─────────────────────
            setDisplayData({
                reportNumber: state?.FILENUMBER || "",
                carrierBookingNumber: state?.carrierBookingNumber || "",
                bookingContactPerson: state?.customerName || "",
                bookingEnteredBy: state?.createdBy || "",
                bookingEnteredDate: state?.createdOn
                    ? moment(state.createdOn).format("D MMM YYYY [AT] hh:mm A")
                    : "",
            });
        } catch (error) {
            console.error("Error fetching booking details:", error);
            dispatch(showToast("Failed to load booking details", "error"));
        } finally {
            dispatch(callLoader(0));
        }
    };

    const formatDateTime = (date, time) => {
        if (!date) return null;
        const safeTime = time || "00:00";
        return new Date(`${date}T${safeTime}`).toISOString();
    };

    const buildFlatPayload = (groupedData, baseData) => {
        const payload = { ...baseData };
        Object.values(groupedData).forEach((group) => {
            group.forEach((item) => {
                const key = item.id;
                payload[key] = item.checked;
                if (item.checked) {
                    payload[`${key}_dt`] = formatDateTime(item.date, item.time);
                    payload[`${key}_dtCurrent`] = new Date().toISOString();
                    payload[`${key}_by`] = "AdminUser";
                } else {
                    payload[`${key}_dt`] = null;
                    payload[`${key}_dtCurrent`] = null;
                    payload[`${key}_by`] = "";
                }
                delete payload[`${key}Dt`];
                delete payload[`${key}Dtcurrent`];
            });
        });
        return payload;
    };

    const _updateStatusAuditDetails = async () => {
        const payload = buildFlatPayload(groupedData, auditData);
        delete payload.bookingEntered;
        delete payload.bookingEnteredDt;

        const changedStatuses = [];
        if (printData?.groupedStatuses && groupedData) {
            Object.entries(groupedData).forEach(([groupKey, items]) => {
                const prevGroup = printData.groupedStatuses[groupKey] || [];
                items.forEach((status, index) => {
                    const prevStatus = prevGroup[index];
                    if (prevStatus && status.checked !== prevStatus.checked) {
                        changedStatuses.push({
                            label: status.label,
                            checked: status.checked,
                            group: groupKey,
                        });
                    }
                });
            });
        }

        try {
            dispatch(callLoader(1));
            const resultData = await dispatch(
                updateImportStatusAuditDetails(payload)
            );
            dispatch(showToast(resultData, "success", true));
            setPrintData(deepClone({ ...auditData, groupedStatuses: groupedData }));

            if (changedStatuses.length > 0) {
                const fileNumber = state?.FILENUMBER;
                const bookingId = state?.bookingId;
                notificationService.statusAuditBulkUpdate(
                    fileNumber,
                    changedStatuses,
                    bookingId
                );
            }
        } catch (error) {
            dispatch(showToast("Failed to update booking details", "error"));
        } finally {
            dispatch(callLoader(0));
        }
    };



    useEffect(() => {
        if (!bookingId) return;
        _getStatusAuditDetails(bookingId, companyId);
    }, [bookingId, companyId]);

    useEffect(() => {
        if (!auditData) return;

        setDisplayData({
            reportNumber: auditData?.FileRefNumber || "",
            carrierBookingNumber: auditData?.CarrierBookingNo || state?.carrierBookingNumber || "",
            bookingContactPerson: auditData?.Consignee || "",
            bookingEnteredBy: auditData?.CreatedBy || "",
            bookingEnteredDate: auditData?.CreatedOn
                ? moment(auditData.CreatedOn).format("D MMM YYYY [AT] hh:mm A")
                : "",
        });
    }, [auditData]);



    return (

        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-4 space-y-4 pb-12"
        >
            {/* Header */}
            <PageHeader
                icon={<CheckSquare />}
                title="Status Audit Report"
                badge={displayData?.reportNumber}
                subtitle="Track and manage booking status checkpoints"
                actions={[
                    {
                        label: "Back",
                        icon: <ArrowLeft />,
                        onClick: () => navigate(-1),
                        variant: "ghost",
                    },
                    "|",
                    {
                        label: "Print",
                        icon: <Printer />,
                        onClick: () =>
                            window.open(
                                `/import-status-audit/print/${state?.companyId || ""}/${state?.bookingId || ""}`,
                                "_blank"
                            ),
                        variant: "tertiary",
                    },
                    {
                        label: "Cancel",
                        icon: <X />,
                        onClick: () => navigate("/import-booking"),
                        variant: "black",
                    },
                ]}
                onSubmit={_updateStatusAuditDetails}
                submitLabel="Save"
                submitIcon={<Save />}
            />
            {/* summary cards */}
            <div
                className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3"
                style={{
                    marginTop: "calc(var(--page-header-height) + 10px)",
                }}
            >
                <AIInsightCard
                    icon={CheckCircle2}
                    title="Completed"
                    value={completedCount}
                    variant="compact"
                    accentKey="emerald"
                />

                <AIInsightCard
                    icon={Clock}
                    title="Pending"
                    value={pendingCount}
                    variant="compact"
                    accentKey="amber"
                />

                <AIInsightCard
                    icon={Target}
                    title="Total"
                    value={totalCount}
                    variant="compact"
                    accentKey="blue"
                />

                <AIInsightCard
                    icon={TrendingUp}
                    title="Progress"
                    value={`${progressPercent}%`}
                    variant="compact"
                    accentKey="red"
                />
            </div>

            {/* Main Content */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
                {/* Left Column - Status Checklist */}
                <div className="xl:col-span-2 space-y-3">
                    {/* Search & Filter */}
                    <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-slate-200 dark:border-slate-800 p-3">
                        <div className="flex flex-col sm:flex-row gap-2">
                            <div className="flex-1 relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <input
                                    type="text"
                                    placeholder="Search checkpoints..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            <div className="flex items-center gap-2 bg-slate-50 dark:bg-slate-800 rounded-lg p-1">
                                {[
                                    { value: "all", label: "All" },
                                    { value: "completed", label: "Completed" },
                                    { value: "pending", label: "Pending" },
                                ].map((filter) => (
                                    <button
                                        key={filter.value}
                                        onClick={() => setFilterStatus(filter.value)}
                                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${filterStatus === filter.value
                                            ? "bg-blue-600 text-white"
                                            : "text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700"
                                            }`}
                                    >
                                        {filter.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Status Groups */}
                    {Object.entries(groupedData).map(([groupKey, items]) => {
                        const filteredItems = getFilteredGroupStatuses(groupKey);
                        const { completed: groupCompleted, total: groupTotal } =
                            getGroupCounts(groupKey);

                        if (filteredItems.length === 0) return null;

                        return (
                            <div
                                key={groupKey}
                                className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800"
                            >
                                <button
                                    onClick={() =>
                                        setExpandedGroups((prev) => ({
                                            ...prev,
                                            [groupKey]: !prev[groupKey],
                                        }))
                                    }
                                    className={`w-full flex items-center justify-between p-4 bg-blue-500 transition-all hover:opacity-90 ${expandedGroups[groupKey] ? "rounded-t-xl" : "rounded-xl"
                                        }`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="bg-white/20 p-2 rounded-lg">
                                            <FileText className="w-5 h-5 text-white" />
                                        </div>
                                        <h3 className="text-base font-semibold text-white">
                                            {ImportStatusAuditGroup[groupKey].label}
                                        </h3>
                                        <span className="px-2 py-0.5 bg-white/20 text-white text-xs font-medium rounded-full">
                                            {groupCompleted}/{groupTotal}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className="w-24 bg-white/20 rounded-full h-2">
                                            <div
                                                className="bg-white h-2 rounded-full transition-all"
                                                style={{
                                                    width: `${groupTotal > 0
                                                        ? (groupCompleted / groupTotal) * 100
                                                        : 0
                                                        }%`,
                                                }}
                                            />
                                        </div>
                                        {expandedGroups[groupKey] ? (
                                            <ChevronUp className="w-5 h-5 text-white/80" />
                                        ) : (
                                            <ChevronDown className="w-5 h-5 text-white/80" />
                                        )}
                                    </div>
                                </button>

                                {expandedGroups[groupKey] && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: "auto", opacity: 1 }}
                                        className="p-4 space-y-2"
                                    >
                                        {filteredItems.map((status) => {
                                            const originalIndex = items.findIndex(
                                                (i) => i.id === status.id
                                            );
                                            return (
                                                <div
                                                    key={status.id}
                                                    className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${status.checked
                                                        ? "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800"
                                                        : "bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 hover:border-blue-300"
                                                        }`}
                                                >
                                                    <button
                                                        onClick={() =>
                                                            handleCheckboxChange(groupKey, originalIndex)
                                                        }
                                                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${status.checked
                                                            ? "bg-emerald-500 border-emerald-500"
                                                            : "border-slate-300 dark:border-slate-600 hover:border-blue-500"
                                                            }`}
                                                    >
                                                        {status.checked && (
                                                            <CheckCircle2 className="w-4 h-4 text-white" />
                                                        )}
                                                    </button>
                                                    <div className="flex-1">
                                                        <label
                                                            className={`text-sm font-medium cursor-pointer ${status.checked
                                                                ? "text-emerald-700 dark:text-emerald-400 line-through"
                                                                : "text-slate-900 dark:text-slate-100"
                                                                }`}
                                                            onClick={() =>
                                                                handleCheckboxChange(groupKey, originalIndex)
                                                            }
                                                        >
                                                            {status.label}
                                                        </label>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <DateInputwithcal
                                                            label=""
                                                            name="date"
                                                            value={status.date}
                                                            onChange={(name, value) =>
                                                                handleDateChange(
                                                                    groupKey,
                                                                    originalIndex,
                                                                    "date",
                                                                    value
                                                                )
                                                            }
                                                            disabled={!status.checked}
                                                        />
                                                        <input
                                                            type="time"
                                                            value={status.time}
                                                            disabled={!status.checked}
                                                            onClick={(e) => e.target.showPicker?.()}
                                                            onChange={(e) =>
                                                                handleDateChange(
                                                                    groupKey,
                                                                    originalIndex,
                                                                    "time",
                                                                    e.target.value
                                                                )
                                                            }
                                                            className="px-2 py-1.5 text-xs bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                        />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </motion.div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Right Column - Info & Actions */}
                <div className="space-y-6">
                    {/* ─── FIX: Booking Contact now uses displayData exactly like StatusAudit uses auditData ─── */}
                    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
                        <div className="bg-blue-600 p-4">
                            <div className="flex items-center gap-3">
                                <div className="bg-white/20 p-2 rounded-lg">
                                    <User className="w-5 h-5 text-white" />
                                </div>
                                <h3 className="text-base font-semibold text-white">
                                    Booking Contact
                                </h3>
                            </div>
                        </div>
                        <div className="p-5">
                            <div className="text-center">
                                <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                                    <User className="w-8 h-8 text-blue-600" />
                                </div>
                                <h4 className="text-xl font-bold text-slate-900 dark:text-white">
                                    {displayData.bookingContactPerson}
                                </h4>
                                <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
                                    Entered on {displayData.bookingEnteredDate || "—"}
                                </p>
                                <p className="text-sm text-slate-500 dark:text-slate-400">
                                    by{" "}
                                    <span className="font-semibold text-blue-600">
                                        {displayData.bookingEnteredBy}
                                    </span>
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Progress Ring */}
                    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 p-6">
                        <div className="text-center">
                            <div className="relative w-32 h-32 mx-auto mb-4">
                                <svg className="w-full h-full transform -rotate-90">
                                    <circle
                                        cx="64"
                                        cy="64"
                                        r="56"
                                        stroke="currentColor"
                                        strokeWidth="12"
                                        fill="none"
                                        className="text-slate-200 dark:text-slate-700"
                                    />
                                    <motion.circle
                                        cx="64"
                                        cy="64"
                                        r="56"
                                        stroke="url(#importGradient)"
                                        strokeWidth="12"
                                        fill="none"
                                        strokeLinecap="round"
                                        initial={{ strokeDasharray: "0 352" }}
                                        animate={{
                                            strokeDasharray: `${(progressPercent / 100) * 352} 352`,
                                        }}
                                        transition={{ duration: 1, ease: "easeOut" }}
                                    />
                                    <defs>
                                        <linearGradient
                                            id="importGradient"
                                            x1="0%"
                                            y1="0%"
                                            x2="100%"
                                            y2="0%"
                                        >
                                            <stop offset="0%" stopColor="#10B981" />
                                            <stop offset="100%" stopColor="#3B82F6" />
                                        </linearGradient>
                                    </defs>
                                </svg>
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="text-center">
                                        <span className="text-3xl font-bold text-slate-900 dark:text-white">
                                            {progressPercent}%
                                        </span>
                                        <p className="text-xs text-slate-500">Complete</p>
                                    </div>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4 mt-4">
                                <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl">
                                    <p className="text-2xl font-bold text-emerald-600">
                                        {completedCount}
                                    </p>
                                    <p className="text-xs text-slate-500 dark:text-slate-400">
                                        Done
                                    </p>
                                </div>
                                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-xl">
                                    <p className="text-2xl font-bold text-amber-600">
                                        {totalCount - completedCount}
                                    </p>
                                    <p className="text-xs text-slate-500 dark:text-slate-400">
                                        Remaining
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Quick Actions */}
                    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
                        <div className="bg-purple-600 p-4">
                            <div className="flex items-center gap-3">
                                <div className="bg-white/20 p-2 rounded-lg">
                                    <BarChart3 className="w-5 h-5 text-white" />
                                </div>
                                <h3 className="text-base font-semibold text-white">
                                    Quick Actions
                                </h3>
                            </div>
                        </div>
                        <div className="p-4 space-y-2">
                            {[
                                {
                                    icon: BarChart3,
                                    label: "Generate Status Report",
                                    color: "bg-blue-600 hover:bg-blue-700",
                                },
                                {
                                    icon: Download,
                                    label: "Export to PDF",
                                    color: "bg-emerald-600 hover:bg-emerald-700",
                                },
                                {
                                    icon: Mail,
                                    label: "Email Status Update",
                                    color: "bg-purple-600 hover:bg-purple-700",
                                },
                                {
                                    icon: Printer,
                                    label: "Print Report",
                                    color: "bg-amber-600 hover:bg-amber-700",
                                },
                            ].map((action, index) => (
                                <a
                                    href={`/import-status-audit/print/${state?.companyId || ""}/${state?.bookingId || ""}`}
                                    target="_blank"
                                    key={index}
                                    className={`w-full px-4 py-3 ${action.color} text-white rounded-xl text-sm font-medium transition-all flex items-center gap-3 shadow-sm hover:shadow-md`}
                                >
                                    <action.icon className="w-4 h-4" />
                                    {action.label}
                                </a>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Sticky Bottom Actions */}
            <div className="p-3 bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 flex items-center justify-between">
                {/* Left side info */}
                <div className="text-sm text-slate-500 dark:text-slate-400">
                    <span className="font-semibold text-emerald-600">
                        {completedCount}
                    </span>{" "}
                    of <span className="font-semibold">{totalCount}</span> checkpoints
                    completed
                </div>

                {/* Right side actions */}
                <div className="flex items-center gap-2">

                    {/* Cancel */}
                    <button
                        type="button"
                        onClick={() => navigate("/import-booking")}
                        className="inline-flex items-center gap-1.5 px-4 h-[38px] text-xs font-medium rounded-md text-slate-600 hover:bg-red-50 transition-all"
                    >
                        <X className="w-4 h-4" />
                        <span className="hidden sm:inline">Cancel</span>
                    </button>

                    {/* Divider */}
                    <div className="w-px h-5 bg-slate-200 dark:bg-slate-700" />
                    {bookingId && (
                        <button
                            type="button"
                            onClick={() =>
                                window.open(
                                    `/import-status-audit/print/${companyId}/${bookingId}`,
                                    "_blank"
                                )
                            }
                            className="inline-flex items-center gap-1.5 px-4 h-[38px] text-xs font-medium rounded-md bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600 dark:hover:bg-slate-600 transition-all"
                        >
                            <Printer className="w-4 h-4" />
                            <span className="hidden sm:inline">Print</span>
                        </button>
                    )}
                    {/* Save / Update */}
                    <CelebrationButton
                        onClick={_updateStatusAuditDetails}
                        label={"Save Changes"}
                        className="inline-flex items-center gap-1.5 text-[12.5px] font-medium px-4 h-[40px] rounded-[9px] transition-all duration-150 bg-green-500 text-white hover:bg-green-700"
                        icon={<Save className="w-4 h-4" />}
                        isSuccess={isAnimate}
                    />

                </div>
            </div>

        </motion.div>
    );
};

export default ImportStatusAudit;